// Kullanıcı rolleri
export enum UserRole {
  ADMIN = 'admin',
  DEVELOPER = 'developer',
  MOD = 'mod',
  CALL_ADMIN = 'call_admin',
  CALL = 'call',
  LIVECHAT_ADMIN = 'livechat_admin',
  LIVECHAT = 'livechat',
  RISK_ADMIN = 'risk_admin',
  RISK = 'risk'
}

// Yetki sistemi için yeni enum
export enum Permission {
  // Admin yetkileri
  USER_MANAGEMENT = 'user_management',
  SYSTEM_SETTINGS = 'system_settings',
  ALL_REPORTS_VIEW = 'all_reports_view',
  ALL_REPORTS_CREATE = 'all_reports_create',
  
  // Developer yetkileri
  PAGE_EDITING = 'page_editing',
  CSS_EDITING = 'css_editing',
  SIDEBAR_MANAGEMENT = 'sidebar_management',
  
  // Mod yetkileri
  CONTENT_MODERATION = 'content_moderation',
  USER_SUPPORT = 'user_support',
  
  // Call yetkileri
  CALL_REPORTS = 'call_reports',
  CALL_MANAGEMENT = 'call_management',
  CALL_ANALYTICS = 'call_analytics',
  
  // LiveChat yetkileri
  LIVECHAT_REPORTS = 'livechat_reports',
  LIVECHAT_MANAGEMENT = 'livechat_management',
  LIVECHAT_ANALYTICS = 'livechat_analytics',
  
  // Risk yetkileri
  RISK_REPORTS = 'risk_reports',
  RISK_MANAGEMENT = 'risk_management',
  RISK_ANALYTICS = 'risk_analytics',
  
  // Chat yetkileri
  CHAT_CREATE_GROUPS = 'chat_create_groups',
  CHAT_MANAGE_GROUPS = 'chat_manage_groups',
  CHAT_SEND_MESSAGES = 'chat_send_messages',
  
  // Rapor yetkileri
  DAILY_REPORTS = 'daily_reports',
  MONTHLY_REPORTS = 'monthly_reports',
  SALES_REPORTS = 'sales_reports',
  HOURLY_REPORTS = 'hourly_reports',
  WORK_SUMMARY_REPORTS = 'work_summary_reports',
  WEEKLY_REPORTS = 'weekly_reports',
  
  // Mesai takibi
  WORK_TIME_TRACKING = 'work_time_tracking',
  WORK_TIME_VIEW = 'work_time_view',
  
  // Panel ayarları
  PANEL_SETTINGS = 'panel_settings'
}

// Yetki tanımları
export interface PermissionDefinition {
  id: Permission;
  name: string;
  description: string;
  category: string;
  isDangerous: boolean;
}

// Rol-yetki eşleştirmesi
export interface RolePermissions {
  role: UserRole;
  permissions: Permission[];
  name: string;
  description: string;
}

// Kullanıcı interface'i
export interface User {
  id: string;
  username: string;
  email: string;
  role: UserRole;
  firstName: string;
  lastName: string;
  isActive: boolean;
  companyName?: string;
  createdAt: Date;
  updatedAt: Date;
  lastLogin?: Date;
  permissions: Permission[]; // Kullanıcının sahip olduğu yetkiler
}

// JWT Token payload
export interface TokenPayload {
  userId: string;
  username: string;
  role: UserRole;
  iat: number;
  exp: number;
}

// Login form data
export interface LoginFormData {
  username: string;
  password: string;
}

// Tema modu
export enum ThemeMode {
  LIGHT = 'light',
  DARK = 'dark'
}

// Mesai kaydı
export interface WorkTimeRecord {
  id: string;
  userId: string;
  date: string;
  startTime: string;
  endTime?: string;
  totalHours?: number;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

// Rapor türleri
export enum ReportType {
  DAILY = 'daily',
  MONTHLY = 'monthly',
  SALES = 'sales',
  CALL = 'call',
  LIVECHAT = 'livechat',
  RISK = 'risk',
  HOURLY = 'hourly',
  WORK_SUMMARY = 'work_summary',
  WEEKLY = 'weekly',
  USER_REPORT = 'user_report',
  EXTERNAL_REPORT = 'external_report',
  WEEKLY_SUMMARY = 'weekly_summary'
}

// Mesai takip türleri
export enum WorkTimeType {
  WORK_START = 'work_start',
  WORK_END = 'work_end',
  BREAK_START = 'break_start',
  BREAK_END = 'break_end'
}

// Mesai takip durumu
export enum WorkTimeStatus {
  WORKING = 'working',
  ON_BREAK = 'on_break',
  OFF_DUTY = 'off_duty'
}

// Rapor interface'i
export interface Report {
  id: string;
  userId: string;
  type: ReportType;
  title: string;
  content: string;
  data: any;
  createdAt: Date;
  updatedAt: Date;
}

// Satış raporu
export interface SalesReport {
  id: string;
  userId: string;
  month: string;
  year: number;
  successfulSales: number;
  failedSales: number;
  totalRevenue: number;
  createdAt: Date;
  updatedAt: Date;
}

// API Response
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// Rapor form verisi
export interface ReportFormData {
  id?: string;
  title: string;
  type: ReportType;
  content: string;
  date: any; // Dayjs | null
  hourlySummary?: string; // Saatlik özet
  workSummary?: string; // Mesai özeti
  totalHours?: number; // Toplam çalışma saati
  hourlyRate?: number; // Saatlik ücret
}

// Sidebar menü öğesi
export interface MenuItem {
  id: string;
  title: string;
  icon: string;
  path: string;
  roles: UserRole[];
}

// Mesai takip interface'leri
export interface WorkTimeEntry {
  id: string;
  userId: string;
  type: WorkTimeType;
  timestamp: Date;
  notes?: string;
  location?: string; // Çalışma yeri (ev ofis, şirket, vb.)
  isManual?: boolean; // Manuel olarak mı eklendi
}

export interface WorkTimeSession {
  id: string;
  userId: string;
  startTime: Date;
  endTime?: Date;
  breakStartTime?: Date;
  breakEndTime?: Date;
  totalWorkMinutes: number;
  totalBreakMinutes: number;
  status: WorkTimeStatus;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface WorkTimeSummary {
  userId: string;
  date: string; // YYYY-MM-DD format
  totalWorkMinutes: number;
  totalBreakMinutes: number;
  sessions: WorkTimeSession[];
  isComplete: boolean; // Gün tamamlandı mı
}

// Chat sistemi için types
export interface ChatGroup {
  id: string;
  name: string;
  description: string;
  createdBy: string; // Admin user ID
  createdAt: Date;
  members: string[]; // User IDs
  isActive: boolean;
  isPinned: boolean; // Başa tutturulmuş mu
  isMuted: boolean; // Sessize alınmış mı
  admins: string[]; // Grup yöneticileri
}

export interface ChatMessage {
  id: string;
  groupId: string;
  userId: string;
  userName: string;
  userRole: UserRole;
  content: string;
  timestamp: Date;
  type: 'text' | 'system' | 'notification';
}

export interface ChatUser {
  id: string;
  name: string;
  role: UserRole;
  isOnline: boolean;
  lastSeen: Date;
  avatar?: string;
}
